(function () {
  /**
   * @typedef {Object} CartItem
   * @property {number} id
   * @property {number} quantity
   */

  function $all (sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  function parseIntSafe (v) {
    var n = parseInt(String(v), 10);
    return isNaN(n) ? 0 : n;
  }

  function getVariantPriceCents (input) {
    if (!input) return 0;

    var fallbackPrice = parseIntSafe(input.getAttribute('data-price-cents'));
    var variantId = parseIntSafe(input.getAttribute('data-variant-id'));
    if (!variantId) return fallbackPrice;

    try {
      var priceApi = /** @type {any} */ (window).igData?.price;
      if (!priceApi || typeof priceApi.getPriceByVariantId !== 'function') {
        return fallbackPrice;
      }

      var testedPrice = priceApi.getPriceByVariantId(variantId);
      if (testedPrice === null || testedPrice === undefined || testedPrice === '') {
        return fallbackPrice;
      }

      var testedPriceAmount = Number(testedPrice);
      return Number.isFinite(testedPriceAmount) && testedPriceAmount >= 0
        ? Math.round(testedPriceAmount * 100)
        : fallbackPrice;
    } catch (error) {
      return fallbackPrice;
    }
  }

  function requestCartWithBackoff (url, options, config) {
    var requestWithBackoff = /** @type {any} */ (window).ThemeCartRequest?.requestWithBackoff;
    return requestWithBackoff ? requestWithBackoff(url, options, config) : fetch(url, options);
  }

  function isCartRateLimitError (error) {
    return Boolean((/** @type {any} */ (window).ThemeCartRequest?.isCartRateLimitError && /** @type {any} */ (window).ThemeCartRequest.isCartRateLimitError(error)) || ((/** @type {any} */ (error)) && (/** @type {any} */ (error)).name === 'CartRateLimitError'));
  }

  function setFormLoadingState (form, isLoading) {
    var buttonContainer = /** @type {any} */ (form.querySelector('add-to-cart-component'));
    if (buttonContainer && typeof buttonContainer.setLoading === 'function') {
      buttonContainer.setLoading(isLoading);
    }

    var button = form.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = isLoading;
      button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  function showAddToCartError (form, message) {
    var errorContainer = form.querySelector('.product-form-text__error');
    if (!(errorContainer instanceof HTMLElement)) return;

    errorContainer.classList.remove('hidden');
    errorContainer.setAttribute('aria-live', 'polite');

    var textNode = Array.from(errorContainer.childNodes).find(function (node) {
      return node.nodeType === Node.TEXT_NODE;
    });

    if (!textNode) {
      textNode = document.createTextNode('');
      errorContainer.appendChild(textNode);
    }

    textNode.textContent = message;
  }

  function clearAddToCartError (form) {
    var errorContainer = form.querySelector('.product-form-text__error');
    if (!(errorContainer instanceof HTMLElement)) return;

    errorContainer.classList.add('hidden');
    errorContainer.removeAttribute('aria-live');

    var textNode = Array.from(errorContainer.childNodes).find(function (node) {
      return node.nodeType === Node.TEXT_NODE;
    });

    if (textNode) {
      textNode.textContent = '';
    }
  }

  function getCurrencyAndFormat (form) {
    var content = (form && form.querySelector && form.querySelector('.zpi-content')) || form.closest('.zpi-content');
    var currency = content && content.getAttribute('data-currency') || undefined;
    var moneyFormat = content && content.getAttribute('data-money-format') || undefined;

    if (!currency) {
      var matches = document.cookie.match(/(?:^|; )cart_currency=([^;]+)/);
      if (matches && matches[1]) {
        currency = decodeURIComponent(matches[1]);
      }
    }

    return { currency: currency, moneyFormat: moneyFormat };
  }

  function countryCodeToLanguageCode (countryCode) {
    var locale = new Intl.Locale('und', { region: countryCode });
    return locale.maximize().language;
  }

  function formatMoney (cents, form) {
    var ctx = getCurrencyAndFormat(form);
    var currency = ctx.currency;
    var amount = cents / 100;

    try {
      var useCurrency = currency || 'USD';
      var region = 'US';
      var matches = document.cookie.match(/(?:^|; )localization=([^;]+)/);
      if (matches && matches[1]) {
        region = decodeURIComponent(matches[1]);
      }

      if (window.Intl && Intl.NumberFormat) {
        var fractionDigits = cents % 100 === 0 ? 0 : 2;
        return new Intl.NumberFormat(countryCodeToLanguageCode(region), {
          style: 'currency',
          currency: useCurrency,
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits
        }).format(amount);
      }
    } catch (error) {}

    if (ctx.moneyFormat) {
      var fmt = ctx.moneyFormat;
      var a2 = amount.toFixed(2);
      var a0 = String(Math.round(amount));
      var aComma = a2.replace('.', ',');
      var aNoTrailing = a2.replace(/\.0+$/, '').replace(/(\.\d)0$/, '$1');

      fmt = fmt.replace(/{{\s*amount_without_trailing_zeros\s*}}/g, aNoTrailing);
      fmt = fmt.replace(/{{\s*amount_with_comma_separator\s*}}/g, aComma);
      fmt = fmt.replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, a0);
      fmt = fmt.replace(/{{\s*amount_no_decimals\s*}}/g, a0);
      fmt = fmt.replace(/{{\s*amount\s*}}/g, a2);
      return fmt;
    }

    return '$' + amount.toFixed(2);
  }

  function updateTotals (form) {
    var mainVariantInput = form.querySelector('input[name="id"][data-variant-id]');
    var mainPrice = getVariantPriceCents(mainVariantInput);
    var mainQtyElRaw = form.querySelector('.zpi-main-quantity-wrap .quantity-selector input[type="number"]');
    var mainQtyEl = /** @type {HTMLInputElement|null} */ (mainQtyElRaw);
    var mainQty = mainQtyEl ? parseIntSafe(mainQtyEl.value) : parseIntSafe(mainVariantInput && mainVariantInput.getAttribute('data-min-qty')) || 1;

    var accessoryHiddenQtys = $all('.zpi-accessory-hidden-qty', form);
    var totalQty = mainQty;
    var totalCents = mainPrice * mainQty;

    accessoryHiddenQtys.forEach(function (h) {
      var hiddenInput = /** @type {HTMLInputElement} */ (h);
      var vId = hiddenInput.getAttribute('data-variant-id');
      var qty = parseIntSafe(hiddenInput.value);
      var assocInput = form.querySelector('input[name="zpi_accessories[]"][data-variant-id="' + vId + '"]');
      var price = getVariantPriceCents(assocInput);

      if (qty > 0) {
        totalQty += qty;
        totalCents += price * qty;
      }
    });

    var qtyNumber = document.querySelector('.zpi-content-total-quantity-number');
    var qtyLabel = document.querySelector('.zpi-content-total-quantity-label');
    var priceEl = document.querySelector('.zpi-content-total-price');

    if (qtyNumber) qtyNumber.textContent = String(totalQty);
    if (qtyLabel) qtyLabel.textContent = totalQty === 1 ? 'item' : 'items';
    if (priceEl) priceEl.textContent = formatMoney(totalCents, form);
  }

  function resetProductForm (form) {
    if (!form) return;

    var mainQtyInput = form.querySelector('.zpi-main-quantity-wrap .quantity-selector input[type="number"]');
    if (mainQtyInput instanceof HTMLInputElement) {
      mainQtyInput.value = '1';
      mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    $all('.zpi-accessory-hidden-qty', form).forEach(function (h) {
      if (h instanceof HTMLInputElement) {
        h.value = '0';
      }
    });

    $all('.zpi-accessory-quantity-wrap', form).forEach(function (wrap) {
      var qtyInput = wrap.querySelector('.quantity-selector input[type="number"]');
      if (qtyInput instanceof HTMLInputElement) {
        qtyInput.value = '0';
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var qtySelectorRoot = wrap.querySelector('.zpi-accessory-quantity-selector') || wrap.querySelector('.quantity-selector');
      if (qtySelectorRoot instanceof HTMLElement) qtySelectorRoot.style.display = 'none';

      var actionsContainer = wrap.closest && wrap.closest('.zpi-accessory-actions') || wrap.parentElement;
      if (actionsContainer) {
        var addBtn = /** @type {HTMLButtonElement|null} */ (actionsContainer.querySelector('.zpi-accessory-add-button'));
        if (addBtn instanceof HTMLElement) addBtn.style.display = '';
      }
    });

    $all('.zpi-upsell-card', form).forEach(function (card) {
      setUpsellQuantity(card, 0);
    });

    updateTotals(form);
  }

  function setUpsellQuantity (card, qty) {
    var max = parseIntSafe(card.getAttribute('data-max-qty')) || 99;
    var value = Math.max(0, Math.min(max, qty));
    var hiddenQty = card.querySelector('.zpi-accessory-hidden-qty');
    var addButton = card.querySelector('.zpi-upsell-card-add');
    var qtyWrap = card.querySelector('.zpi-upsell-card-qty');
    var qtyValue = card.querySelector('.zpi-upsell-card-qty-value');
    var plusButton = card.querySelector('.zpi-upsell-card-qty-button[data-step="1"]');

    if (hiddenQty instanceof HTMLInputElement) hiddenQty.value = String(value);
    if (qtyValue) qtyValue.textContent = String(value);
    if (addButton instanceof HTMLElement) addButton.hidden = value > 0;
    if (qtyWrap instanceof HTMLElement) qtyWrap.hidden = value === 0;
    if (plusButton instanceof HTMLButtonElement) plusButton.disabled = value >= max;
  }

  function bindUpsellToggles (form) {
    $all('.zpi-upsell-card', form).forEach(function (card) {
      var hiddenQty = card.querySelector('.zpi-accessory-hidden-qty');
      var addButton = card.querySelector('.zpi-upsell-card-add');
      var currentQty = function () {
        return hiddenQty instanceof HTMLInputElement ? parseIntSafe(hiddenQty.value) : 0;
      };

      if (addButton instanceof HTMLButtonElement) {
        addButton.addEventListener('click', function () {
          setUpsellQuantity(card, 1);
          updateTotals(form);

          // The Add button is now hidden; keep focus inside the card
          var plus = card.querySelector('.zpi-upsell-card-qty-button[data-step="1"]');
          if (plus instanceof HTMLButtonElement) plus.focus();
        });
      }

      $all('.zpi-upsell-card-qty-button', card).forEach(function (button) {
        button.addEventListener('click', function () {
          setUpsellQuantity(card, currentQty() + parseIntSafe(button.getAttribute('data-step')));
          updateTotals(form);

          if (currentQty() === 0 && addButton instanceof HTMLButtonElement) addButton.focus();
        });
      });
    });
  }

  function bindAccessorySelectors (form) {
    $all('.zpi-accessory-quantity-wrap', form).forEach(function (wrap) {
      var variantId = wrap.getAttribute('data-variant-id');
      var hiddenQty = form.querySelector('.zpi-accessory-hidden-qty[data-variant-id="' + variantId + '"]');
      if (!hiddenQty) return;

      var qtyInput = wrap.querySelector('.quantity-selector input[type="number"]');
      if (!qtyInput) return;

      var qtyEl = /** @type {HTMLInputElement} */ (qtyInput);
      if (hiddenQty instanceof HTMLInputElement) {
        hiddenQty.value = String(parseIntSafe(qtyEl.value));
      }

      var actionsContainer = wrap.closest && wrap.closest('.zpi-accessory-actions > .zpi-accessory-quantity-wrap') || wrap.parentElement;
      var addButton = actionsContainer && /** @type {HTMLButtonElement|null} */ (actionsContainer.querySelector('.zpi-accessory-add-button'));
      if (!addButton && actionsContainer) {
        addButton = /** @type {HTMLButtonElement} */ (document.createElement('button'));
        addButton.type = 'button';
        addButton.className = 'zpi-accessory-add-button';
        addButton.textContent = '+ Add';
        actionsContainer.insertBefore(addButton, actionsContainer.firstChild);
      }

      var toggleVisibility = function () {
        var qty = 0;
        if (hiddenQty instanceof HTMLInputElement) qty = parseIntSafe(hiddenQty.value);
        var qtySelectorRoot = wrap.querySelector('.zpi-accessory-quantity-selector') || wrap.querySelector('.quantity-selector');
        if (qtySelectorRoot instanceof HTMLElement) qtySelectorRoot.style.display = qty === 0 ? 'none' : '';
        if (addButton instanceof HTMLButtonElement) addButton.style.display = qty === 0 ? '' : 'none';
      };

      toggleVisibility();

      var onChange = function () {
        if (hiddenQty instanceof HTMLInputElement) {
          hiddenQty.value = String(parseIntSafe(qtyEl.value));
        }

        setTimeout(function () {
          updateTotals(form);
          toggleVisibility();
        }, 0);
      };

      qtyInput.addEventListener('change', onChange);
      qtyInput.addEventListener('input', onChange);

      var plus = wrap.querySelector('.quantity-selector .quantity-increment');
      var minus = wrap.querySelector('.quantity-selector .quantity-decrement');

      if (plus) {
        plus.addEventListener('click', function () {
          setTimeout(onChange, 10);
        });
      }

      if (minus) {
        minus.addEventListener('click', function () {
          setTimeout(onChange, 10);
        });
      }

      if (addButton instanceof HTMLElement) {
        addButton.addEventListener('click', function () {
          if (qtyInput instanceof HTMLInputElement) {
            qtyInput.value = '1';
            if (hiddenQty instanceof HTMLInputElement) hiddenQty.value = '1';
            qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
            setTimeout(function () {
              updateTotals(form);
              toggleVisibility();
            }, 0);
          }
        });
      }
    });
  }

  function buildItemsFromForm (form) {
    var mainVariantInput = form.querySelector('input[name="id"][data-variant-id]');
    var mainVariantId = mainVariantInput && parseIntSafe(mainVariantInput.getAttribute('data-variant-id'));
    var mainQtyElRaw = form.querySelector('.zpi-main-quantity-wrap .quantity-selector input[type="number"]');
    var mainQtyEl = /** @type {HTMLInputElement|null} */ (mainQtyElRaw);
    var mainQty = mainQtyEl ? parseIntSafe(mainQtyEl.value) : parseIntSafe(mainVariantInput && mainVariantInput.getAttribute('data-min-qty')) || 1;

    /** @type {CartItem[]} */
    var items = [];
    if (mainVariantId && mainQty > 0) {
      items.push({ id: mainVariantId, quantity: mainQty });
    }

    $all('.zpi-accessory-hidden-qty', form).forEach(function (h) {
      var hiddenInput = /** @type {HTMLInputElement} */ (h);
      var vId = parseIntSafe(hiddenInput.getAttribute('data-variant-id'));
      var qty = parseIntSafe(hiddenInput.value);
      if (qty > 0) {
        items.push({ id: vId, quantity: qty });
      }
    });

    return items;
  }

  function dispatchCartSuccess (form) {
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: {} } }));
      document.addEventListener('cart:updated-items', function _zpi_onUpdated () {
        setTimeout(function () {
          document.dispatchEvent(new CustomEvent('cart:open', { detail: {} }));
          try { resetProductForm(form); } catch (error) {}
        }, 50);
      }, { once: true });
    }, 50);
  }

  async function submitItemsBatch (form) {
    var items = buildItemsFromForm(form);
    if (items.length === 0) return;

    var response = await requestCartWithBackoff('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }, { maxRetryTimeMs: 30000 });

    if (!response.ok) {
      throw await response.json();
    }

    await response.json();
  }

  async function submitItemsIndividually (form) {
    var items = buildItemsFromForm(form);

    for (var index = 0; index < items.length; index += 1) {
      var item = items[index];
      var response = await requestCartWithBackoff('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, quantity: item.quantity })
      }, { maxRetryTimeMs: 30000 });

      if (!response.ok) {
        throw await response.json();
      }

      await response.json();
    }
  }

  function hijackForm (form) {
    if (!(form instanceof HTMLFormElement)) return;

    var activeRequests = new WeakSet();

    bindAccessorySelectors(form);
    bindUpsellToggles(form);

    document.querySelectorAll('.zpi-accessory-hidden-qty').forEach(function (h) {
      if (!(h instanceof HTMLElement)) return;
      if (!form.contains(h)) form.appendChild(h);
    });

    var mainQtyInput = form.querySelector('.zpi-main-quantity-wrap .quantity-selector input[type="number"]');
    if (mainQtyInput) {
      var onMain = function () {
        setTimeout(function () {
          updateTotals(form);
        }, 0);
      };
      mainQtyInput.addEventListener('change', onMain);
      mainQtyInput.addEventListener('input', onMain);
    }

    updateTotals(form);

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (activeRequests.has(form) || form.dataset.zpiCartLoading === 'true') {
        return;
      }

      activeRequests.add(form);
      form.dataset.zpiCartLoading = 'true';
      clearAddToCartError(form);
      setFormLoadingState(form, true);

      (async function () {
        try {
          await submitItemsBatch(form);
          dispatchCartSuccess(form);
          updateTotals(form);
          clearAddToCartError(form);
        } catch (error) {
          if (isCartRateLimitError(error)) {
            showAddToCartError(form, 'failed to add to cart, please try again');
            return;
          }

          console.error('Error bulk adding items', error);

          try {
            await submitItemsIndividually(form);
            dispatchCartSuccess(form);
            updateTotals(form);
            clearAddToCartError(form);
          } catch (fallbackError) {
            if (isCartRateLimitError(fallbackError)) {
              showAddToCartError(form, 'failed to add to cart, please try again');
              return;
            }

            console.error('Error adding items individually', fallbackError);
            showAddToCartError(form, 'failed to add to cart, please try again');
            (/** @type {HTMLFormElement} */ (form)).submit();
          }
        } finally {
          form.dataset.zpiCartLoading = 'false';
          setFormLoadingState(form, false);
          activeRequests.delete(form);
        }
      })();
    });
  }

  function init () {
    var sectionId = /** @type {any} */ (window).sectionId || '';
    var targetFormEl = document.querySelector('form[data-type="add-to-cart-form"]') || document.querySelector('form#BuyButtonsWithFallback-ProductForm-' + sectionId) || document.querySelector('form');

    if (targetFormEl instanceof HTMLFormElement) {
      hijackForm(targetFormEl);
    } else if (targetFormEl) {
      hijackForm(/** @type {HTMLFormElement} */ (targetFormEl));
    }

    var updateIntelligemsTotal = function () {
      if (targetFormEl instanceof HTMLFormElement) {
        updateTotals(targetFormEl);
      }
    };

    if ((/** @type {any} */ (window).igData?.price)) {
      updateIntelligemsTotal();
    } else {
      window.addEventListener('ig:ready', updateIntelligemsTotal, { once: true });
    }

    document.addEventListener('quantity-selector:update', function (ev) {
      var tgt = ev.target;
      if (!(tgt instanceof HTMLElement)) return;

      var form = tgt.closest && tgt.closest('form[data-type="add-to-cart-form"]');
      if (!form) {
        form = document.querySelector('form[data-type="add-to-cart-form"]') || targetFormEl;
      }
      if (!form) return;

      var accessoryWrap = tgt.closest && tgt.closest('.zpi-accessory-quantity-wrap');
      if (accessoryWrap) {
        var vId = accessoryWrap.getAttribute('data-variant-id');
        var qtyInput = accessoryWrap.querySelector('input[type="number"]');
        var hidden = form.querySelector('.zpi-accessory-hidden-qty[data-variant-id="' + vId + '"]');
        if (hidden instanceof HTMLInputElement && qtyInput instanceof HTMLInputElement) {
          hidden.value = String(parseIntSafe(qtyInput.value));
        }

        (function () {
          var wrap = document.querySelector('.zpi-accessory-quantity-wrap[data-variant-id="' + vId + '"]');
          if (!wrap) return;
          var hiddenGlob = form.querySelector('.zpi-accessory-hidden-qty[data-variant-id="' + vId + '"]') || document.querySelector('.zpi-accessory-hidden-qty[data-variant-id="' + vId + '"]');
          var qtySelectorRoot = wrap.querySelector('.zpi-accessory-quantity-selector') || wrap.querySelector('.quantity-selector');
          var actionsContainer = wrap.closest && wrap.closest('.zpi-accessory-actions') || wrap.parentElement;
          var addBtn = actionsContainer && /** @type {HTMLButtonElement|null} */ (actionsContainer.querySelector('.zpi-accessory-add-button'));
          var qty = hiddenGlob instanceof HTMLInputElement ? parseIntSafe(hiddenGlob.value) : 0;
          if (qtySelectorRoot instanceof HTMLElement) qtySelectorRoot.style.display = qty === 0 ? 'none' : '';
          if (addBtn instanceof HTMLButtonElement) addBtn.style.display = qty === 0 ? '' : 'none';
        })();
      }

      setTimeout(function () {
        if (form instanceof HTMLFormElement) {
          updateTotals(form);
        } else if (targetFormEl instanceof HTMLFormElement) {
          updateTotals(targetFormEl);
        }
      }, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();